import './layout.css'
import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { Link, Outlet, useNavigate } from '@tanstack/react-router'
import {
  LayoutGrid, Activity, Wallet, Radio, Map, Gauge,
  Handshake, PieChart, Users, Building2, Star, Newspaper,
  Bell, Crown, BarChart2, FileText, CreditCard, Settings,
  HelpCircle, Menu, Search, LogOut, KeyRound, Gem, Webhook, Info,
  Tag, Eye, StickyNote, Code, MapPin, TrendingUp, MessageCircle, Zap,
  Contact, ShieldCheck, LifeBuoy, DollarSign,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { applyAccountLocale } from '../i18n'
import { NAV_ITEMS, isSection, isNavItem, withHotspotBadge, withPremiumTag, type NavItem } from './nav'
import { ThemeToggle } from '../components/ThemeToggle'
import { useTheme, type Theme } from '../providers/ThemeProvider'
import { Avatar } from '../components/Avatar'
import { useUserStore, accountToUser } from '../stores/userStore'
import { useAuthStore } from '../stores/authStore'
import { queryClient } from '../lib/query-client'
import { StatusBanner } from '../features/status/StatusBanner'
import { DemoBanner } from '../components/DemoBanner'
import { ImpersonationBanner } from '../components/ImpersonationBanner'
import { PriceTicker } from '../components/PriceTicker'
import { CommandPalette } from '../components/CommandPalette'
import { AppFooter } from './AppFooter'
import { ToastContainer } from '../components/Toast/ToastContainer'
import { NotificationCenter } from '../components/NotificationCenter'
import { ConnectionStatusDot } from '../features/dashboard/ConnectionStatusDot'
import { useUserPermissions } from '../hooks/useUserPermissions'
import { useAccount } from '../features/settings/useAccount'
import { SidebarQuotaMeters } from './SidebarQuotaMeters'

// --- Icon map: nav icon string → Lucide component ---

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  LayoutGrid,
  Activity,
  Wallet,
  Radio,
  Map,
  Gauge,
  Handshake,
  PieChart,
  Users,
  Contact,
  Building2,
  Star,
  Newspaper,
  Bell,
  Crown,
  BarChart2,
  FileText,
  CreditCard,
  Settings,
  HelpCircle,
  KeyRound,
  Gem,
  Webhook,
  Info,
  Tag,
  Eye,
  StickyNote,
  // Welle-2-Review F7: Icon des Speedtest-Eintrags (Sektion Hotspots).
  Zap,
  // Story 0.2: bisher fehlende Icons — top-cities/top-spots/support rendern
  // sonst ein leeres Icon-Placeholder-Span.
  Code,
  MapPin,
  TrendingUp,
  MessageCircle,
  ShieldCheck,
  LifeBuoy,
  // Admin-Umsatzseite; ohne Eintrag hier rendert NavIcon still ein leeres Span.
  DollarSign,
}

function NavIcon({ name }: { name: string }) {
  const Icon = ICON_MAP[name]
  if (!Icon) return <span className="ic" aria-hidden="true" />
  return <Icon size={16} className="ic" aria-hidden="true" />
}

// --- Sidebar nav item ---

function SidebarNavItem({ item }: { item: NavItem }) {
  return (
    <Link
      to={item.href}
      className="nav-item"
      activeProps={{ className: 'nav-item active' }}
      activeOptions={{ exact: false }}
      data-testid={`nav-item-${item.id}`}
      data-nav={item.id}
    >
      <NavIcon name={item.icon} />
      <span>{item.label}</span>
      {item.badge && <span className="badge">{item.badge}</span>}
      {item.tag && !item.badge && <span className="badge">{item.tag}</span>}
    </Link>
  )
}

// --- AppShell ---

type AppShellProps = {
  children?: ReactNode
}

// Map from section string in nav.ts → shell translation key
const SECTION_KEY_MAP: Record<string, string> = {
  'Übersicht': 'nav.sections.overview',
  'Hotspots': 'nav.sections.hotspots',
  'Einnahmen': 'nav.sections.income',
  'Einblicke': 'nav.sections.insights',
  'Premium': 'nav.sections.premium',
  'Konto': 'nav.sections.account',
  'Admin': 'nav.sections.admin',
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation('shell')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { user, setUser, clearUser } = useUserStore()
  const initials = user?.avatarInitials ?? 'U'
  const { clearAuth } = useAuthStore()
  const { applyBackendTheme } = useTheme()
  const navigate = useNavigate()
  const { canViewAdminItems } = useUserPermissions()

  // F-094: Hotspot-Badge dynamisch aus der echten Hotspot-Anzahl befüllen
  // (GET /api/v2/account/me → hotspots_count) statt hartkodierter "12".
  const { account } = useAccount()
  const navItems = withPremiumTag(withHotspotBadge(NAV_ITEMS, account?.hotspots_count), account?.is_premium)

  // FIN-SWEEP (shell-dashboard): userStore war ein hartkodierter Demo-Stub, der
  // nie mit echten Account-Daten befüllt wurde. Sobald der Account geladen ist,
  // Name/E-Mail/Initialen/Plan/Admin-Flag aus GET /api/v2/account/me übernehmen.
  useEffect(() => {
    if (account) {
      setUser(accountToUser(account))
    }
  }, [account, setUser])

  // Mac-Finding W2-1 (Theme-Persistenz): Die frühere Variante rief hier
  // setTheme(account.theme) auf, nur bewacht durch einen komponenten-lokalen
  // Ref. Bei jedem Remount der AppShell (Routenwechsel, die das Layout neu
  // aufbauen, Reload) wurde die BE-Präferenz (z.B. dark) erneut angewandt und
  // überschrieb die explizite Nutzerwahl (light) aus localStorage.
  // Jetzt: applyBackendTheme ist ein reiner Fallback — es wendet die
  // BE-Präferenz NUR an, wenn keine explizite lokale Wahl existiert, und
  // schreibt selbst nie in localStorage. Damit ist der Aufruf idempotent und
  // Routenwechsel/Remounts können das Theme nie mehr zurücksetzen.
  useEffect(() => {
    if (account) {
      applyBackendTheme(account.theme as Theme)
    }
  }, [account, applyBackendTheme])

  // Issue #55 — users.locale wurde nie ausgewertet: ein deutsches Konto in
  // einem englischsprachigen Browser blieb komplett auf Englisch. Analog zum
  // Theme-Fallback oben: applyAccountLocale wendet die BE-Praeferenz NUR an,
  // solange keine explizite lokale Sprachwahl existiert (EXPLICIT_LANGUAGE_KEY).
  useEffect(() => {
    if (account) {
      applyAccountLocale(account.locale)
    }
  }, [account])

  // Außen-Klick schließt das User-Menü (Konto/Einstellungen/Abmelden)
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  // F-SEARCH-1 / HD (Runde 6): Globale Suche laeuft jetzt ueber die
  // Cmd+K-Kommandopalette (src/components/CommandPalette.tsx). Die Topbar
  // traegt nur noch das Lupen-Icon als Ausloeser — das fruehere Inline-Feld
  // entfaellt zugunsten einer Palette mit Gruppen (Hotspots, Wallets,
  // Kontakte, Tags) und Tastaturnavigation.
  const [paletteOpen, setPaletteOpen] = useState(false)
  // Tastenkuerzel-Hinweis plattformabhaengig (macOS zeigt ⌘K, sonst Strg+K)
  const shortcutHint =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
      ? '⌘K'
      : 'Ctrl K'
  const openPalette = useCallback(() => setPaletteOpen(true), [])
  const closePalette = useCallback(() => setPaletteOpen(false), [])

  // Globaler Cmd+K / Strg+K-Shortcut
  useEffect(() => {
    function handleKeydown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((v) => !v)
      }
    }
    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
  }, [])

  // Close drawer on resize above 900px breakpoint
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 900) {
        setDrawerOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  function closeDrawer() {
    setDrawerOpen(false)
  }

  async function handleLogout() {
    // Fire-and-forget revoke (AC-7 — auch bei Fehler wird lokal ausgeloggt)
    try {
      const { accessToken } = useAuthStore.getState()
      const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID
      const clientSecret = import.meta.env.VITE_OAUTH_CLIENT_SECRET
      // F-K.1: ohne konfigurierte OAuth-Client-Credentials liefert Doorkeeper
      // ohnehin nur 400 (invalid_client) — Request dann gar nicht erst senden.
      if (accessToken && clientId && clientSecret) {
        fetch(`${import.meta.env.VITE_API_BASE_URL}/oauth/revoke`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            token: accessToken,
            client_id: clientId,
            client_secret: clientSecret,
          }),
        }).catch(() => {}) // ignorieren
      }
    } finally {
      // FE-ROB-02: clearAuth() entfernt seit dem Fix auch ht_demo_mode /
      // ht_demo_token (authStore.ts). Vorher blieb der Demo-Modus nach dem
      // Abmelden aktiv und der nachfolgende echte Login lief weiter unter dem
      // geteilten Demo-Account.
      clearAuth()
      clearUser()
      queryClient.clear()
      navigate({ to: '/login' })
    }
  }

  return (
    <>
      {/* STATUS_BANNER_SLOT — W0.6 */}
      <div id="status-banner-slot">
        <StatusBanner />
      </div>

      {/* DEMO_BANNER_SLOT — W3.1 */}
      <DemoBanner />

      {/* IMPERSONATION_BANNER_SLOT — Admin-Userverwaltung */}
      <ImpersonationBanner />

      <ToastContainer />

      <div className="page" data-testid="app-shell">
        {/* Sidebar */}
        <aside
          className={`sidebar${drawerOpen ? ' is-open' : ''}`}
          aria-label={t('sidebar.ariaLabel')}
          data-testid="app-sidebar"
          data-open={drawerOpen ? 'true' : 'false'}
        >
          {/* Logo */}
          <div className="sidebar-logo">
            <div className="logo-mark">H</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '-0.01em' }}>
                HeliumTracker
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>v3.0 · Solana</div>
            </div>
          </div>

          {/* Nav items */}
          <nav aria-label={t('sidebar.nav.ariaLabel')} data-testid="sidebar-nav">
            {navItems.map((entry, i) => {
              if (isSection(entry)) {
                // Die Admin-Sektion enthaelt ausschliesslich adminOnly-Eintraege
                // - ohne Admin-Rechte waere sie eine leere Kopfzeile ohne Inhalt.
                if (entry.section === 'Admin' && !canViewAdminItems) {
                  return null
                }
                const sectionKey = SECTION_KEY_MAP[entry.section]
                return (
                  <div
                    key={`section-${i}`}
                    className="sidebar-section"
                    data-testid={`nav-section-${sectionKey ? sectionKey.split('.').pop() : entry.section}`}
                  >
                    {sectionKey ? t(sectionKey) : entry.section}
                  </div>
                )
              }
              if (isNavItem(entry)) {
                // Skip admin-only items if user doesn't have permission (S10-03)
                if (entry.adminOnly && !canViewAdminItems) {
                  return null
                }
                const translatedItem = {
                  ...entry,
                  label: t(`nav.items.${entry.id}`, { defaultValue: entry.label }),
                }
                return <SidebarNavItem key={entry.id} item={translatedItem} />
              }
              return null
            })}
          </nav>

          {/* Footer: Kontingent-Meter (Story 0.2) + User */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            {/* AC-3: zwei getrennte Fortschrittsbalken (Mobile + IoT) */}
            <SidebarQuotaMeters />

            <div
              style={{
                borderTop: '1px solid var(--border)',
                padding: '12px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flex: 'none',
              }}
              data-testid="sidebar-user"
            >
              <Avatar initials={initials} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{user?.name ?? t('sidebar.user.fallbackName')}</div>
                <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                  {user?.plan ?? t('sidebar.user.fallbackPlan')}
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Drawer overlay — closes sidebar on click */}
        <div
          className={`drawer-overlay${drawerOpen ? ' is-open' : ''}`}
          onClick={closeDrawer}
          aria-hidden="true"
          data-testid="drawer-overlay"
        />

        {/* Main area: topbar + content */}
        <div className="main">
          <header className="topbar" data-testid="app-topbar">
            {/* Mobile hamburger toggle */}
            <button
              className="btn btn-icon btn-ghost"
              id="ht-mobile-toggle"
              aria-label={t('topbar.toggleMenu')}
              aria-expanded={drawerOpen}
              data-testid="mobile-menu-toggle"
              onClick={() => setDrawerOpen((prev) => !prev)}
              style={{ marginRight: 4 }}
            >
              <Menu size={20} />
            </button>

            {/* Such-Trigger (Lupe) — oeffnet die Cmd+K-Kommandopalette */}
            <button
              type="button"
              className="btn btn-ghost topbar-search-trigger"
              onClick={openPalette}
              aria-label={t('topbar.search.ariaLabel')}
              aria-haspopup="dialog"
              aria-expanded={paletteOpen}
              data-testid="global-search-trigger"
            >
              <Search size={16} aria-hidden="true" />
              <span className="topbar-search-trigger__label">
                {t('topbar.search.triggerLabel')}
              </span>
              <kbd className="topbar-search-trigger__kbd" aria-hidden="true">
                {shortcutHint}
              </kbd>
            </button>

            <div style={{ flex: 1 }} />

            {/* Preisticker (Desktop, nur eingeloggter Bereich) */}
            <PriceTicker />



            {/* SSE connection status */}
            <ConnectionStatusDot />

            {/* Theme toggle */}
            <ThemeToggle />

            {/* Notifications bell */}
            <NotificationCenter />

            {/* Logout */}
            <button
              className="btn btn-icon btn-ghost"
              aria-label={t('topbar.logout')}
              onClick={() => { void handleLogout() }}
              data-testid="logout-button"
            >
              <LogOut size={18} />
            </button>

            {/* Avatar mit Konto-Menü (FIN-SWEEP shell-dashboard: Klick öffnete vorher nichts) */}
            <div ref={userMenuRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn btn-icon btn-ghost"
                style={{ padding: 0, borderRadius: '50%' }}
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-label={t('topbar.userMenu.ariaLabel')}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                data-testid="user-menu-trigger"
              >
                <Avatar initials={initials} />
              </button>

              {userMenuOpen && (
                <div
                  role="menu"
                  aria-label={t('topbar.userMenu.ariaLabel')}
                  data-testid="user-menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    right: 0,
                    minWidth: 180,
                    background: 'var(--surface-2, #1e1e2e)',
                    border: '1px solid var(--border, #333)',
                    borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    zIndex: 9999,
                    overflow: 'hidden',
                  }}
                >
                  <Link
                    to="/account/subscription"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    data-testid="user-menu-account"
                    style={{
                      display: 'block',
                      padding: '10px 16px',
                      textDecoration: 'none',
                      color: 'var(--text)',
                      fontSize: 13,
                    }}
                  >
                    {t('topbar.userMenu.account')}
                  </Link>
                  <Link
                    to="/settings"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    data-testid="user-menu-settings"
                    style={{
                      display: 'block',
                      padding: '10px 16px',
                      textDecoration: 'none',
                      color: 'var(--text)',
                      fontSize: 13,
                    }}
                  >
                    {t('topbar.userMenu.settings')}
                  </Link>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setUserMenuOpen(false)
                      void handleLogout()
                    }}
                    data-testid="user-menu-logout"
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 16px',
                      background: 'none',
                      border: 'none',
                      borderTop: '1px solid var(--border, #333)',
                      color: 'var(--text)',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {t('topbar.userMenu.logout')}
                  </button>
                </div>
              )}
            </div>
          </header>

          {/* Content slot */}
          <main id="main-content">
            <div className="content">
              {children ?? <Outlet />}
            </div>
            <AppFooter />
          </main>
        </div>
      </div>

      {/* Cmd+K-Kommandopalette (globale, user-scopierte Suche) */}
      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </>
  )
}

export default AppShell
